import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Chip, Paper, 
    Alert, IconButton, Tooltip, CircularProgress
} from '@mui/material';

import FactCheckIcon from '@mui/icons-material/FactCheck';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import GroupIcon from '@mui/icons-material/Group';

import * as API from '../../api'; // Điều chỉnh đường dẫn import API cho đúng với cấu trúc folder của bạn
import ConfirmDialog from '../ConfirmDialog'; // Điều chỉnh đường dẫn
import ExchangeRateDialog from './ExchangeRateDialog';

const SystemControl = ({ onRefreshData, showToast }) => {
    const navigate = useNavigate();

    // State cho các Dialog
    const [controlDialogOpen, setControlDialogOpen] = useState(false);
    const [scanResultOpen, setScanResultOpen] = useState(false);
    const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
    const [redundantOpen, setRedundantOpen] = useState(false);
    const [redundantData, setRedundantData] = useState({ bookings: [], customers: [] });
    const [isCleaning, setIsCleaning] = useState(false);
    const [rateDialogOpen, setRateDialogOpen] = useState(false);
    
    // State dữ liệu & xử lý
    const [scanResults, setScanResults] = useState([]);
    const [isScanning, setIsScanning] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [uploadingId, setUploadingId] = useState(null); // ID item đang upload để hiện loading

    // --- 1. LOGIC ĐỒNG BỘ CÔNG NỢ ---
    const handleConfirmSync = async () => {
        setConfirmSyncOpen(false);
        setIsSyncing(true);
        try {
            const res = await API.syncLiabilityStatus();
            showToast(res.data.message, 'success');
            if (onRefreshData) onRefreshData(); // Refresh lại dashboard sau khi sync
        } catch (err) {
            showToast("Lỗi đồng bộ: " + (err.response?.data?.error || err.message), 'error');
        } finally {
            setIsSyncing(false);
        }
    };

    // --- 2. LOGIC QUÉT LỖI ---
    const handleScanErrors = async () => {
        setControlDialogOpen(false);
        setScanResultOpen(true);
        setIsScanning(true);
        setScanResults([]);

        try {
            const res = await API.client.get('/management/scan_missing_files/'); 
            setScanResults(res.data.items || []);
            if (res.data.count === 0) {
                showToast("Hệ thống sạch! Không tìm thấy file lỗi.", 'success');
            }
        } catch (err) {
            showToast("Lỗi khi quét: " + (err.response?.data?.error || err.message), 'error');
        } finally {
            setIsScanning(false);
        }
    };

    // --- 3. LOGIC ĐĂNG LẠI BILL (Ghi đè) ---
    const handleReUpload = async (item, file) => {
        if (!file) return;
        setUploadingId(item.id); // Bật loading cho dòng này

        const formData = new FormData();
        // item.field là tên trường trong DB (ví dụ: 'bill_image', 'deposit_bill_image')
        formData.append(item.field, file);

        try {
            let endpoint = '';
            // Xác định endpoint dựa trên loại đối tượng
            if (item.type === 'PAYABLE') {
                endpoint = `/payables/${item.id}/`;
            } else if (item.type === 'PAYMENT') {
                endpoint = `/booking_payments/${item.id}/`; // Lưu ý: Cần check lại url trong urls.py, thường là booking_payments hoặc booking-payments
            } else if (item.type === 'BOOKING') {
                endpoint = `/bookings/${item.id}/`;
            }

            if (endpoint) {
                await API.client.patch(endpoint, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                showToast(`Đã cập nhật lại ảnh cho ${item.ref}`, 'success');
                
                // Cập nhật lại list kết quả (loại bỏ item đã sửa xong)
                setScanResults(prev => prev.filter(r => !(r.id === item.id && r.type === item.type)));
            }
        } catch (err) {
            console.error(err);
            showToast("Lỗi upload: " + err.message, 'error');
        } finally {
            setUploadingId(null);
        }
    };

    const handleScanRedundant = async () => {
        setControlDialogOpen(false);
        setRedundantOpen(true);
        setIsScanning(true);
        setRedundantData({ bookings: [], customers: [] });

        try {
            const res = await API.client.get('/management/scan_redundant_data/');
            setRedundantData(res.data);
            if (res.data.total_booking === 0 && res.data.total_customer === 0) {
                showToast("Hệ thống sạch! Không có dữ liệu rác.", 'success');
            }
        } catch (err) {
            showToast("Lỗi khi quét: " + (err.response?.data?.error || err.message), 'error');
        } finally {
            setIsScanning(false);
        }
    };

    const handleCleanupBookings = async () => {
        if (!redundantData.bookings.length) return;
        
        if (!window.confirm(`Bạn có chắc muốn xóa vĩnh viễn ${redundantData.bookings.length} booking rỗng này không?`)) return;

        setIsCleaning(true);
        try {
            const payload = {
                items: redundantData.bookings.map(b => ({ type: 'EMPTY_BOOKING', id: b.id }))
            };
            
            const res = await API.client.post('/management/cleanup_redundant_data/', payload);
            showToast(res.data.message, 'success');
            
            handleScanRedundant();
            if (onRefreshData) onRefreshData();

        } catch (err) {
            showToast("Lỗi dọn dẹp: " + err.message, 'error');
        } finally {
            setIsCleaning(false);
        }
    };

    // --- 4. LOGIC XEM BOOKING (Điều hướng đúng) ---
    const handleViewBooking = (bookingCode) => {
        navigate('/form', { state: { bookingCodeToLoad: bookingCode } });
    };

    return (
        <>
            {/* NÚT CHÍNH TRÊN TOOLBAR */}
            <Button
                variant="contained"
                color="warning"
                startIcon={isSyncing ? <CircularProgress size={20} color="inherit"/> : <FactCheckIcon />}
                onClick={() => setControlDialogOpen(true)}
                disabled={isSyncing}
                sx={{ fontWeight: 'bold', color: 'white', whiteSpace: 'nowrap' }}
            >
                {isSyncing ? 'Đang đồng bộ...' : 'Kiểm soát Hệ thống'}
            </Button>

            <Dialog 
                open={controlDialogOpen} 
                onClose={() => setControlDialogOpen(false)}
                maxWidth="xs" fullWidth
            >
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', color: '#007373' }}>
                    KIỂM SOÁT HỆ THỐNG
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0px 24px' }}>
                        <Button 
                            variant="outlined" size="large"
                            startIcon={<SyncProblemIcon color="primary"/>}
                            onClick={() => { setControlDialogOpen(false); setConfirmSyncOpen(true); }}
                            sx={{ justifyContent: 'flex-start', py: 1.5, border: '1px solid #ddd' }}
                        >
                            <Box sx={{ textAlign: 'left', ml: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold">Đồng bộ Trạng thái Công nợ</Typography>
                                <Typography variant="caption" color="text.secondary">Rà soát trùng lặp & Tính lại trạng thái TT</Typography>
                            </Box>
                        </Button>

                        <Button 
                            variant="outlined" size="large"
                            startIcon={<ImageNotSupportedIcon color="error"/>}
                            onClick={handleScanErrors}
                            sx={{ justifyContent: 'flex-start', py: 1.5, border: '1px solid #ddd' }}
                        >
                            <Box sx={{ textAlign: 'left', ml: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold">Quét lỗi Dữ liệu (File ảnh)</Typography>
                                <Typography variant="caption" color="text.secondary">Tìm các Bill bị mất file gốc trên Server</Typography>
                            </Box>
                        </Button>

                        <Button 
                            variant="outlined" size="large"
                            startIcon={<DeleteSweepIcon sx={{ color: '#ed6c02' }}/>}
                            onClick={handleScanRedundant}
                            sx={{ justifyContent: 'flex-start', py: 1.5, border: '1px solid #ddd' }}
                        >
                            <Box sx={{ textAlign: 'left', ml: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold">Quét Booking Rỗng & Trùng lặp</Typography>
                                <Typography variant="caption" color="text.secondary">Tìm Booking không dữ liệu và Khách trùng SĐT</Typography>
                            </Box>
                        </Button>

                        <Button 
                            variant="outlined" size="large"
                            startIcon={<CurrencyExchangeIcon sx={{ color: '#007373' }}/>}
                            onClick={() => { 
                                setControlDialogOpen(false); // Đóng menu chính
                                setRateDialogOpen(true);     // Mở dialog tỷ giá
                            }}
                            sx={{ justifyContent: 'flex-start', py: 1.5, border: '1px solid #ddd' }}
                        >
                            <Box sx={{ textAlign: 'left', ml: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold">Cấu hình Tỷ giá (USD/VND)</Typography>
                                <Typography variant="caption" color="text.secondary">Cập nhật tỷ giá quy đổi hệ thống</Typography>
                            </Box>
                        </Button>

                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button onClick={() => setControlDialogOpen(false)} color="inherit">Đóng</Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG KẾT QUẢ QUÉT LỖI */}
            <Dialog 
                open={scanResultOpen} 
                onClose={() => setScanResultOpen(false)}
                maxWidth="md" fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">KẾT QUẢ QUÉT LỖI DỮ LIỆU</Typography>
                    <Button onClick={() => setScanResultOpen(false)} color="inherit">Đóng</Button>
                </DialogTitle>
                <DialogContent dividers>
                    {isScanning ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Đang quét toàn bộ hệ thống...</Typography>
                        </Box>
                    ) : (
                        <>
                            {scanResults.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4, color: 'success.main' }}>
                                    <CheckCircleIcon sx={{ fontSize: 50, mb: 1 }} />
                                    <Typography variant="h6">Hệ thống an toàn!</Typography>
                                    <Typography>Toàn bộ link ảnh trong Database đều tồn tại file vật lý.</Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        Phát hiện <b>{scanResults.length}</b> bản ghi bị lỗi file ảnh (File gốc bị xóa hoặc di chuyển).
                                    </Alert>
                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Loại</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Mã tham chiếu</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Chi tiết</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Trường lỗi</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', minWidth: 160 }}>Hành động</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {scanResults.map((item, index) => (
                                                    <TableRow key={index} hover>
                                                        <TableCell>
                                                            <Chip 
                                                                label={item.type} 
                                                                color={item.type === 'BOOKING' ? 'primary' : (item.type === 'PAYABLE' ? 'error' : 'warning')} 
                                                                size="small" 
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>{item.ref}</TableCell>
                                                        <TableCell>{item.name}</TableCell>
                                                        <TableCell>
                                                            <code style={{ backgroundColor: '#eee', padding: '2px 4px', borderRadius: 4, fontSize: '0.8rem' }}>{item.field}</code>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                {/* NÚT 1: ĐĂNG LẠI BILL */}
                                                                <Tooltip title="Upload đè lại ảnh Bill mới">
                                                                    <IconButton 
                                                                        color="primary" 
                                                                        component="label" 
                                                                        size="small"
                                                                        disabled={uploadingId === item.id}
                                                                    >
                                                                        {uploadingId === item.id ? <CircularProgress size={20} /> : <CloudUploadIcon fontSize="small" />}
                                                                        <input 
                                                                            type="file" 
                                                                            hidden 
                                                                            accept="image/*"
                                                                            onChange={(e) => handleReUpload(item, e.target.files[0])}
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>

                                                                {/* NÚT 2: XEM BOOKING */}
                                                                <Tooltip title="Mở Booking này">
                                                                    <IconButton
                                                                        color="primary" 
                                                                        component="label" 
                                                                        size="small"
                                                                        onClick={() => handleViewBooking(item.ref)}
                                                                    >
                                                                        <ArrowForwardIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* --- DIALOG MỚI: KẾT QUẢ QUÉT RÁC --- */}
            <Dialog 
                open={redundantOpen} 
                onClose={() => setRedundantOpen(false)}
                maxWidth="md" fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">DỮ LIỆU CẦN XỬ LÝ</Typography>
                    <Button onClick={() => setRedundantOpen(false)} color="inherit">Đóng</Button>
                </DialogTitle>
                <DialogContent dividers>
                    {isScanning ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>Đang phân tích dữ liệu...</Typography>
                        </Box>
                    ) : (
                        <Box>
                            {/* PHẦN 1: BOOKING RỖNG */}
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" color="error">
                                        1. Booking Rỗng / Không dữ liệu ({redundantData.bookings.length})
                                    </Typography>
                                    {redundantData.bookings.length > 0 && (
                                        <Button 
                                            variant="contained" color="error" size="small"
                                            startIcon={isCleaning ? <CircularProgress size={16} color="inherit"/> : <DeleteSweepIcon />}
                                            onClick={handleCleanupBookings}
                                            disabled={isCleaning}
                                        >
                                            Xóa tất cả Booking rỗng
                                        </Button>
                                    )}
                                </Box>
                                {redundantData.bookings.length === 0 ? (
                                    <Alert severity="success">Không có booking rỗng.</Alert>
                                ) : (
                                    <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead><TableRow><TableCell>Mã BK</TableCell><TableCell>Chi tiết</TableCell></TableRow></TableHead>
                                            <TableBody>
                                                {redundantData.bookings.map((item, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell sx={{fontWeight:'bold'}}>{item.ref}</TableCell>
                                                        <TableCell>{item.name} <br/> <Typography variant="caption" color="textSecondary">{item.desc}</Typography></TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Paper>
                                )}
                            </Box>

                            {/* PHẦN 2: KHÁCH HÀNG TRÙNG LẶP */}
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="warning.main" sx={{ mb: 1 }}>
                                    2. Khách hàng Trùng lặp SĐT ({redundantData.customers.length})
                                </Typography>
                                {redundantData.customers.length === 0 ? (
                                    <Alert severity="success">Không có khách hàng trùng lặp SĐT.</Alert>
                                ) : (
                                    <>
                                        <Alert severity="warning" sx={{ mb: 1 }}>Hệ thống chỉ liệt kê, vui lòng kiểm tra và xử lý thủ công (Gộp khách) để tránh mất dữ liệu lịch sử.</Alert>
                                        <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead><TableRow><TableCell>SĐT Trùng</TableCell><TableCell>Số lượng</TableCell><TableCell>Mã KH bị trùng</TableCell></TableRow></TableHead>
                                                <TableBody>
                                                    {redundantData.customers.map((item, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell sx={{fontWeight:'bold'}}>{item.ref}</TableCell>
                                                            <TableCell><Chip label={item.name} size="small" color="warning"/></TableCell>
                                                            <TableCell>{item.desc}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Paper>
                                    </>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <ExchangeRateDialog 
                open={rateDialogOpen} 
                onClose={() => setRateDialogOpen(false)} 
            />

            <ConfirmDialog 
                open={confirmSyncOpen}
                title="Xác nhận đồng bộ"
                content="Hệ thống sẽ rà soát lại toàn bộ Công nợ để loại bỏ trùng lặp và tính toán lại. Quá trình này có thể mất vài giây."
                onClose={() => setConfirmSyncOpen(false)}
                onConfirm={handleConfirmSync}
                confirmText="Đồng ý chạy"
                confirmColor="warning"
            />
        </>
    );
};

export default SystemControl;